/**
 * Ortak "ölçü kartı → süzülmüş liste" (A1, 24 Ekim 2026; pazarlama çalışma merkezi deseninden genelleştirildi). Kural:
 *  - Kart GERÇEK bir bağlantıdır (`<a>`; klavye, odak, yeni sekme, adres kopyalama, mobil dokunma).
 *  - Kart sayısı = açılan listenin toplamı: hedef adresi sunucunun verdiği süzgeçtir (`?suzgec=`, aynı ACL/dönem/saat dilimi).
 *  - Sayım tavana takıldıysa "N+" (kesin sayı gibi sunulmaz); erişilebilir adda "N ve üzeri (sayım tavanı)".
 *  - Sıfır da bağlantıdır: boş süzülmüş listeye gider (genel listeye sessizce düşmez).
 *  - Erişilebilir ad: etiket + sayı (+ çağıranın notu) + "Listeyi aç". Renk tek başına anlam taşımaz (ikon + etiket + açıklama).
 *  - Yüzeyler yalnız mevcut semantik ve etiket token'larıdır (yeni renk yok); hover/odak `cardInteractive` (hareket azaltma saygılı).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { encodeListFilter, LIST_FILTER_PARAM, type FilterAst } from '@castinflow/shared';
import { cardInteractive, cn } from '@castinflow/ui';
import { listHref } from '../routes';
import { commonText as C } from './common-strings';

/** Sayı metni: kesin değilse alt sınır "N+" (kesin sayı gibi sunulmaz) */
export function countText(n: number, exact = true): string {
  const s = n.toLocaleString('tr-TR');
  return exact ? s : `${s}+`;
}

/** Kayıt türünün süzülmüş liste adresi; `null` süzgeç → süzgeçsiz liste */
export function filteredListHref(type: string, filter: FilterAst | null): string {
  return filter === null
    ? listHref(type)
    : `${listHref(type)}?${LIST_FILTER_PARAM}=${encodeListFilter(filter)}`;
}

export type MetricTone =
  | 'primary'
  | 'blue'
  | 'indigo'
  | 'info'
  | 'warning'
  | 'orange'
  | 'purple'
  | 'success'
  | 'danger'
  | 'neutral';

/** Pastel vurgu yüzeyleri (yalnız mevcut semantik ve etiket token'ları). Metin rengi her zeminde varsayılan metin tonudur (AA). */
export const METRIC_SURFACE: Record<MetricTone, { card: string; icon: string }> = {
  primary: { card: 'bg-primary-subtle border-primary/25', icon: 'text-primary' },
  blue: { card: 'bg-tag-blue-bg border-tag-blue-fg/25', icon: 'text-tag-blue-fg' },
  indigo: { card: 'bg-tag-indigo-bg border-tag-indigo-fg/25', icon: 'text-tag-indigo-fg' },
  info: { card: 'bg-info-subtle border-info/25', icon: 'text-info' },
  warning: { card: 'bg-warning-subtle border-warning/30', icon: 'text-warning' },
  orange: { card: 'bg-tag-orange-bg border-tag-orange-fg/25', icon: 'text-tag-orange-fg' },
  purple: { card: 'bg-tag-purple-bg border-tag-purple-fg/25', icon: 'text-tag-purple-fg' },
  success: { card: 'bg-success-subtle border-success/25', icon: 'text-success' },
  danger: { card: 'bg-danger-subtle border-danger/25', icon: 'text-danger' },
  neutral: { card: 'bg-surface border-border', icon: 'text-fg-muted' },
};

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

export interface MetricLinkCardProps {
  /** Hedef liste adresi (süzgeçli) */
  to: string;
  label: string;
  count: number;
  exact: boolean;
  /** Kısa açıklama (görünür; `strip` düzeninde geniş ekranda yalnız ekran okuyucuda ve ipucunda) */
  hint: string;
  icon: ReactNode;
  tone: MetricTone;
  testId: string;
  /** Erişilebilir ada ve ipucuna eklenen not (ör. "· 12.500 TRY (tahsilat değil)") */
  note?: string;
  /**
   * Düzen: `strip` pazarlama çalışma merkezinin kompakt şeridi (≥ 1280 px tek satır, ikon ve açıklama gizli); `tile` pano kartı
   * (ikon solda, büyük sayı, açıklama, yön oku).
   */
  layout?: 'strip' | 'tile';
  /** Sayının altındaki ek içerik (ör. para birimi başına tutar) */
  children?: ReactNode;
}

export function MetricLinkCard({
  to,
  label,
  count,
  exact,
  hint,
  icon,
  tone,
  testId,
  note = '',
  layout = 'strip',
  children,
}: MetricLinkCardProps) {
  const value = countText(count, exact);
  const s = METRIC_SURFACE[tone];
  const common = {
    to,
    title: hint + note,
    // Tavanlı sayıda erişilebilir ad "10.000 ve üzeri (sayım tavanı)" — "+" işareti ikinci kez okunmaz
    'aria-label': C.openList(label, exact ? value : C.capped(countText(count))) + note,
    'data-testid': testId,
    'data-count': count,
    'data-exact': exact ? 'true' : 'false',
  } as const;
  if (layout === 'tile')
    return (
      <Link
        {...common}
        className={cn(
          // Telefonda ikon üstte (etiket bölünmeden tam genişlikte), geniş ekranda solda
          'group relative flex h-full min-h-[7.5rem] flex-col items-start gap-2 rounded-card border px-3 py-3 text-left shadow-card sm:flex-row sm:gap-3 sm:px-4 sm:py-3.5',
          s.card,
          cardInteractive,
          FOCUS,
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-surface shadow-card sm:h-11 sm:w-11',
            s.icon,
          )}
        >
          {icon}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-start justify-between gap-2">
            <span className="min-w-0 text-sm font-semibold text-fg">{label}</span>
            <ArrowUpRight
              size={16}
              aria-hidden="true"
              className="absolute right-3 top-3 shrink-0 text-fg-subtle transition-transform sm:static duration-fast motion-safe:group-hover:-translate-y-0.5 motion-safe:group-hover:translate-x-0.5 group-hover:text-primary"
            />
          </span>
          <span
            className="text-xl font-semibold tabular-nums text-fg sm:text-display"
            data-testid={`${testId}-value`}
          >
            {value}
          </span>
          <span className="text-sm text-fg-muted">{hint}</span>
          {children}
        </span>
      </Link>
    );
  return (
    <Link
      {...common}
      className={cn(
        'rounded-card border shadow-card',
        s.card,
        cardInteractive,
        // Masaüstünde kompakt: kuyruk (günlük işin merkezi) ilk ekranda kalsın
        'flex h-full min-h-[6.5rem] flex-col gap-1 px-3.5 py-3 text-left sm:min-h-[7rem] sm:px-4 lg:min-h-0 lg:py-2.5',
        FOCUS,
      )}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-fg xl:min-h-[2.5rem]">{label}</span>
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-surface shadow-card lg:h-8 lg:w-8 xl:hidden',
            s.icon,
          )}
        >
          {icon}
        </span>
      </span>
      <span
        className="text-xl font-semibold tabular-nums text-fg sm:text-display lg:text-title"
        data-testid={`${testId}-value`}
      >
        {value}
      </span>
      <span className="text-sm text-fg-muted lg:truncate xl:sr-only">{hint}</span>
      {children}
    </Link>
  );
}

/**
 * Tablo hücresindeki sayı bağlantısı (ör. kaynak kalitesi: "3" → o kaynağın nitelikli adayları). Sayı = liste kuralı aynıdır;
 * erişilebilir ad bağlamı söyler ("Web formu — nitelikli: 3. Listeyi aç"). Sıfır da bağlantıdır (boş süzülmüş liste).
 */
export function CountLink({
  to,
  count,
  exact,
  context,
  testId,
}: {
  to: string;
  count: number;
  exact: boolean;
  /** Erişilebilir ad bağlamı (satır + sütun) */
  context: string;
  testId?: string;
}) {
  const value = countText(count, exact);
  return (
    <Link
      to={to}
      aria-label={C.countLink(context, exact ? value : C.capped(countText(count)))}
      className={cn(
        // Dokunma hedefi: satır yüksekliğinde en az 44 px genişlik, sağa yaslı rakam
        'inline-flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-end rounded-sm px-1 font-medium tabular-nums underline-offset-2 hover:underline',
        count === 0 ? 'text-fg-muted' : 'text-primary',
        FOCUS,
      )}
      data-testid={testId}
      data-count={count}
    >
      {value}
    </Link>
  );
}
